import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Clock } from 'lucide-react';
import { useKiprData } from '@/lib/kiprData';
import { isLearnerKipPlayable, catalogStatusLabel } from '@/lib/catalogAvailability';

const KipLibrary: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<string>('');
  const { kips, guardians, loading, error, isAdmin, clients, colleagues } = useKiprData();

  const playable = (id: string) => {
    const kip = kips.find((k) => k.id === id);
    return kip ? isLearnerKipPlayable(kip, clients, colleagues, isAdmin) : false;
  };

  const categories = ['Communication', 'Empathy', 'Accuracy', 'Education', 'Progress', 'Collaboration'];
  
  // Get unique clients from kips that have clients
  const clientFilterNames = Array.from(
    new Set(
      kips
        .filter((kip) => kip.client)
        .map((kip) => kip.client!.name),
    ),
  ).sort();

  const filteredKips = kips.filter(kip => {
    const matchesSearch = kip.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         kip.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || kip.category === selectedCategory;
    const matchesClient = !selectedClient || (kip.client && kip.client.name === selectedClient);
    return matchesSearch && matchesCategory && matchesClient;
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold font-poppins text-[#1A1A1A] mb-2">Kip Library</h1>
        <p className="text-[#555555] font-inter">Choose a training scenario to begin</p>
      </div>

      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#555555]" size={20} />
        <input
          type="text"
          placeholder="Search scenarios..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 border border-[#DADADA] rounded-md focus:outline-none focus:ring-2 focus:ring-[#121212] focus:border-transparent"
        />
      </div>

      {/* Filter Dropdowns */}
      <div className="flex flex-wrap gap-4">
        <div className="min-w-48">
          <label className="block text-sm font-medium text-[#555555] mb-2">Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border border-[#DADADA] rounded-md focus:outline-none focus:ring-2 focus:ring-[#121212] focus:border-transparent bg-white"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        
        <div className="min-w-48">
          <label className="block text-sm font-medium text-[#555555] mb-2">Client</label>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="w-full px-3 py-2 border border-[#DADADA] rounded-md focus:outline-none focus:ring-2 focus:ring-[#121212] focus:border-transparent bg-white"
          >
            <option value="">All Clients</option>
            {clientFilterNames.map((client) => (
              <option key={client} value={client}>
                {client}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Scenario Grid */}
      <div className="space-y-3">
        {loading && (
          <div className="bg-white p-4 rounded-lg shadow-sm text-[#555555]">
            Loading scenario catalog...
          </div>
        )}
        {filteredKips.map((kip) => (
          <div key={kip.id} className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-4 mb-2">
                  <span className="inline-block bg-[#DADADA] text-[#1A1A1A] text-xs font-medium px-3 py-1 rounded-full">
                    {kip.category}
                  </span>
                  <div className="flex items-center space-x-2 text-[#555555] text-sm">
                    <Clock size={16} />
                    <span>{kip.duration}</span>
                  </div>
                  {kip.completed && (
                    <span className="text-green-600 text-xs font-medium">Completed</span>
                  )}
                  {!playable(kip.id) && (
                    <span className="text-amber-800 text-xs font-medium bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                      {kip.availability !== 'available' ? catalogStatusLabel(kip.availability) : 'Coming soon'}
                    </span>
                  )}
                  {kip.client && (
                    <span className="text-blue-600 text-xs font-medium">
                      Client: {kip.client.name}
                    </span>
                  )}
                  {kip.guardianId && (
                    <span className="text-purple-600 text-xs font-medium">
                      Guardian: {guardians.find(g => g.id === kip.guardianId)?.name}
                    </span>
                  )}
                </div>
                <h3 className="font-semibold font-poppins text-[#1A1A1A] mb-1">{kip.title}</h3>
                <p className="text-[#555555] text-sm line-clamp-2">{kip.description}</p>
              </div>
              <div className="ml-6 flex-shrink-0">
                <button
                  onClick={() => playable(kip.id) && navigate(`/scenario/${kip.id}`)}
                  disabled={!playable(kip.id)}
                  className={`px-6 py-2 rounded-md font-medium transition-colors ${
                    playable(kip.id)
                      ? 'bg-[#121212] text-white hover:bg-[#2A2A2A]'
                      : 'bg-[#ECECEC] text-[#888] cursor-not-allowed'
                  }`}
                >
                  {kip.completed ? 'Practice Again' : playable(kip.id) ? 'Start Kip' : 'Coming soon'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KipLibrary;